import PT from 'prop-types';
import React from 'react';

import { Button } from '~/eds/src/components/Button/Button';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { CrmType } from '~/enums/CrmType';
import { useIntl } from 'react-intl';
import Box from '~/ui/components/Box/Box';
import Chip from '~/ui/components/Chip/Chip';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';

function IntegrationStatusAndActions({
  disabled,
  integration,
  isActive = false,
  isLoading = false,
  isPending = false,
  onConnect,
  onDelete,
  onToggleCollapse,
}) {
  const intl = useIntl();
  const { id, error } = integration;
  const items = [];

  if (disabled) {
    items.push(
      <Button
        disabled={disabled}
        text={intl.formatMessage({
          id: 'IntegrationStatusAndActions.connect1',
          defaultMessage: 'Connect',
          description: 'Connect button text',
        })}
        variant="action"
      />,
    );
  } else {
    // integration status
    if (!isActive && !id) {
      items.push(
        <Button
          disabled={disabled}
          onClick={onConnect}
          text={intl.formatMessage({
            id: 'IntegrationStatusAndActions.connect2',
            defaultMessage: 'Connect',
            description: 'Connect button text',
          })}
          variant="action"
        />,
      );
    } else if (error) {
      items.push(
        <Tooltip key="error" tooltip={error.message}>
          <Chip
            text={intl.formatMessage({
              id: 'IntegrationStatusAndActions.validationError',
              defaultMessage: 'Validation error',
              description: 'Validation error chip text',
            })}
            variant="danger"
          />
        </Tooltip>,
      );
    } else if (isActive && isPending) {
      items.push(
        <Chip
          key="pending"
          text={intl.formatMessage({
            id: 'IntegrationStatusAndActions.pending',
            defaultMessage: 'Pending',
            description: 'Pending status chip',
          })}
          variant="warning"
        />,
      );
    } else if (isActive && !id) {
      items.push(
        <Chip
          key="not-connected"
          text={intl.formatMessage({
            id: 'IntegrationStatusAndActions.notConnected',
            defaultMessage: 'Not connected',
            description: 'Not connected status chip',
          })}
          variant="info"
        />,
      );
    } else if (id) {
      items.push(
        <Chip
          key="connected"
          text={intl.formatMessage({
            id: 'IntegrationStatusAndActions.connected',
            defaultMessage: 'Connected',
            description: 'Connected status chip',
          })}
          variant="success"
        />,
      );
    }

    // delete status
    if (id) {
      items.push(
        <Box key="delete" ml={6} pl={8} sx={{ borderLeft: 'border' }}>
          {isLoading ? (
            <Icon icon="loading" />
          ) : (
            <IconButton
              disabled={isLoading}
              icon="trash"
              label={intl.formatMessage({
                id: 'IntegrationStatusAndActions.remove',
                defaultMessage: 'remove',
                description: 'Remove button label',
              })}
              onClick={onDelete}
            />
          )}
        </Box>,
      );
    }
  }

  return (
    <FlexLayout alignItems="center" space={2} onClick={onToggleCollapse}>
      {items}
    </FlexLayout>
  );
}

// TODO: make a common StatusAndActions component that takes different integration object types
IntegrationStatusAndActions.propTypes = {
  integration: PT.shape({
    id: PT.string,
    config: PT.object.isRequired,
    error: PT.shape({ message: PT.string.isRequired }),
    isUserLoggedIn: PT.bool,
    type: PT.oneOf(Object.values(CrmType)),
  }).isRequired,
  isActive: PT.bool,
  isLoading: PT.bool,
  isPending: PT.bool,
  onConnect: PT.func.isRequired,
  onDelete: PT.func.isRequired,
  onToggleCollapse: PT.func.isRequired,
};

export default IntegrationStatusAndActions;
